import { ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoTooltipIcon } from '../../icons/icons';
import RequiredFieldAsterisk from '../../RequiredFieldAsterisk';
import {
    SusyFormFieldBackgroundLightnessType,
    SusyFormFieldLabelPosition,
} from './SusyFormFieldDefinitions';

interface ISusyFormFieldProps {
    children: ReactNode;
    required?: boolean;
    classes?: string;
    label?: {
        fieldIdentifier?: string;
        text?: string;
        position?: SusyFormFieldLabelPosition;
        dataTestId?: string;
    };
    tooltipText?: string;
    formGroupProps?: {
        containerDataTestId?: string;
        errorDataTestId?: string;
    };
    backgroundLightness?: SusyFormFieldBackgroundLightnessType;
    hintMessage?: string;
    hasErrors?: boolean;
    errorMessage?: string;
    dataTestId?: string;
}

const SusyFormField = (props: ISusyFormFieldProps) => {
    const getWrapperClasses = () => {
        const classes = [props.classes ?? ''];
        if (props.label?.position === SusyFormFieldLabelPosition.LEFT) {
            classes.push('label-position-left');
        }
        if (
            props.backgroundLightness ===
            SusyFormFieldBackgroundLightnessType.LIGHT
        ) {
            classes.push('field-background-light');
        }
        if (props.hasErrors) {
            classes.push('has-errors');
        }

        return classes;
    };

    const getLabel = () => {
        return (
            <>
                <label
                    htmlFor={props.label?.fieldIdentifier}
                    data-testid={props.label?.dataTestId}
                    className='susy-input-label'
                >
                    {props.label?.text}
                </label>
                {props.required && (
                    <span className='ml-1'>
                        <RequiredFieldAsterisk />
                    </span>
                )}
            </>
        );
    };

    const getOverlayTooltip = () => {
        return (
            <OverlayTrigger
                placement={'top'}
                overlay={<Tooltip>{props.tooltipText}</Tooltip>}
                trigger={['hover', 'click']}
            >
                <span className='tooltip-icon pl-2 d-flex align-self-center ml-2'>
                    <InfoTooltipIcon />
                </span>
            </OverlayTrigger>
        );
    };

    const classes = getWrapperClasses();

    return (
        <div
            className={`form-group susy-form-group ${classes.join(' ')}`}
            data-testid={props.formGroupProps?.containerDataTestId}
        >
            {(props.label || props.tooltipText) && (
                <div className='formgroup-label-and-infotooltip-container d-inline-flex align-items-center'>
                    {props.label && getLabel()}
                    {props.tooltipText && getOverlayTooltip()}
                </div>
            )}

            <div className='formgroup-input-container'>
                {props.children}
                {props.hintMessage && (
                    <div className='formgroup-inputcontainer-hint-message susy-input-text'>
                        {props.hintMessage}
                    </div>
                )}
                {props.hasErrors && props.errorMessage && (
                    <div
                        className='formgroup-inputcontainer-error-message susy-input-text'
                        data-testid={props.formGroupProps?.errorDataTestId}
                    >
                        {props.errorMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SusyFormField;
